import React, { useState } from 'react';
import './GoogleReviewsInsight.css';

const text = (v) => (typeof v === 'string' ? v.trim() : '');
const list = (v) => (Array.isArray(v) ? v : []);
const httpsUrl = (v) => (typeof v === 'string' && v.startsWith('https://') ? v : null);

const SENTIMENT = {
  positive: { label: 'Mostly positive', icon: 'fa-face-smile' },
  mixed: { label: 'Mixed', icon: 'fa-face-meh' },
  negative: { label: 'Mostly negative', icon: 'fa-face-frown' },
};

const TREND = {
  improving: { label: 'Improving', icon: 'fa-arrow-trend-up' },
  declining: { label: 'Declining', icon: 'fa-arrow-trend-down' },
  stable: { label: 'Stable', icon: 'fa-arrows-left-right' },
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Stars({ value }) {
  const full = Math.round(Number(value) || 0);
  return (
    <span className="gr-stars" aria-label={`${value} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={`fa fa-star${n <= full ? '' : ' gr-star--off'}`} />
      ))}
    </span>
  );
}

function Histogram({ histogram }) {
  const counts = [5, 4, 3, 2, 1].map((n) => Number(histogram?.[n]) || 0);
  const max = Math.max(...counts, 1);
  return (
    <div className="gr-histo">
      {counts.map((count, i) => {
        const star = 5 - i;
        return (
          <div key={star} className="gr-histo-row">
            <span className="gr-histo-star">{star}</span>
            <span className="gr-histo-track">
              <span className={`gr-histo-fill gr-histo-fill--${star}`} style={{ width: `${(count / max) * 100}%` }} />
            </span>
            <span className="gr-histo-count">{count.toLocaleString('en-US')}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Google Review Audit block for the Intelligence view (data: researchIntelligence.googleReviewAudit). */
export default function GoogleReviewsInsight({ audit }) {
  const [showReviews, setShowReviews] = useState(false);
  if (!audit?.place) return null;

  const { place, stats = {}, analysis = {} } = audit;
  const topics = list(audit.topics).slice(0, 8);
  const reviews = list(audit.reviews);
  const complaints = list(analysis.complaints);
  const strengths = list(analysis.strengths);
  const weaknesses = list(analysis.weaknesses).map(text).filter(Boolean);
  const starters = list(analysis.conversationStarters).map(text).filter(Boolean);
  const customFindings = list(analysis.customFindings).map(text).filter(Boolean);
  const sentiment = SENTIMENT[analysis.sentiment];
  const trend = TREND[stats.trend];
  const mapsUrl = httpsUrl(place.mapsUrl);
  const auditedAt = formatDate(audit.auditedAt);

  return (
    <section className="ir-section gr">
      <div className="ir-section-label">
        <i className="fa fa-star-half-stroke" />Google Reviews
        {place.reviewCount != null && (
          <span className="ir-section-count">{Number(place.reviewCount).toLocaleString('en-US')}</span>
        )}
      </div>

      <div className="gr-card">
        <div className="gr-overview">
          <div className="gr-score">
            <span className="gr-score-value">{place.rating != null ? Number(place.rating).toFixed(1) : '–'}</span>
            <Stars value={place.rating} />
            {text(place.name) && <span className="gr-score-name" title={text(place.address)}>{text(place.name)}</span>}
          </div>
          {stats.histogram && <Histogram histogram={stats.histogram} />}
        </div>

        <div className="gr-badges">
          {stats.negativeShare != null && (
            <span className={`gr-badge ${stats.negativeShare >= 15 ? 'gr-badge--bad' : ''}`} title="Share of 1–2 star reviews">
              <i className="fa fa-thumbs-down" />{stats.negativeShare}% negative
            </span>
          )}
          {stats.ownerReplyRate != null && (
            <span className={`gr-badge ${stats.ownerReplyRate < 20 ? 'gr-badge--bad' : 'gr-badge--good'}`} title="Owner replies in the sampled reviews">
              <i className="fa fa-reply" />{stats.ownerReplyRate}% answered
            </span>
          )}
          {trend && (
            <span className={`gr-badge gr-badge--trend-${stats.trend}`} title="Newest reviews vs. overall rating">
              <i className={`fa ${trend.icon}`} />{trend.label}
            </span>
          )}
          {sentiment && (
            <span className={`gr-badge gr-badge--sent-${analysis.sentiment}`}>
              <i className={`fa ${sentiment.icon}`} />{sentiment.label}
            </span>
          )}
        </div>

        {text(analysis.summary) && <p className="gr-summary">{text(analysis.summary)}</p>}

        {complaints.length > 0 && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fa fa-triangle-exclamation" />Top complaints</span>
            <ul className="gr-themes">
              {complaints.map((c, i) => (
                <li key={i} className="gr-theme">
                  <span className={`gr-sev gr-sev--${c.severity || 'medium'}`} title={`${c.severity || 'medium'} severity`} />
                  <div className="gr-theme-body">
                    <span className="gr-theme-title">
                      {text(c.theme)}
                      {c.mentions != null && <span className="gr-theme-count">{c.mentions}×</span>}
                    </span>
                    {text(c.evidence) && <span className="gr-theme-evidence">„{text(c.evidence)}“</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {weaknesses.length > 0 && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fa fa-wrench" />Weak spots</span>
            <ul className="gr-bullets">{weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}

        {strengths.length > 0 && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fa fa-thumbs-up" />Praised</span>
            <div className="gr-chips">
              {strengths.map((s, i) => (
                <span key={i} className="gr-chip gr-chip--good" title={text(s.evidence)}>
                  {text(s.theme)}{s.mentions != null && <em>{s.mentions}×</em>}
                </span>
              ))}
            </div>
          </div>
        )}

        {topics.length > 0 && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fab fa-google" />Google topics</span>
            <div className="gr-chips">
              {topics.map((t, i) => (
                <span key={i} className="gr-chip">{text(t.label)}<em>{t.mentions}</em></span>
              ))}
            </div>
          </div>
        )}

        {text(analysis.ownerResponse) && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fa fa-reply" />Owner replies</span>
            <p className="gr-text">{text(analysis.ownerResponse)}</p>
          </div>
        )}

        {text(analysis.customerProfile) && (
          <div className="gr-block">
            <span className="gr-block-label"><i className="fa fa-users" />Who reviews</span>
            <p className="gr-text">{text(analysis.customerProfile)}</p>
          </div>
        )}

        {customFindings.length > 0 && (
          <div className="gr-block">
            <span className="gr-block-label" title={text(audit.settings?.customFocus)}>
              <i className="fa fa-magnifying-glass" />Your question
            </span>
            {text(audit.settings?.customFocus) && <p className="gr-question">{text(audit.settings.customFocus)}</p>}
            <ul className="gr-bullets">{customFindings.map((f, i) => <li key={i}>{f}</li>)}</ul>
          </div>
        )}

        {(text(analysis.salesAngle) || starters.length > 0) && (
          <div className="gr-angle">
            {text(analysis.salesAngle) && (
              <p className="gr-angle-main"><i className="fa fa-lightbulb" />{text(analysis.salesAngle)}</p>
            )}
            {starters.length > 0 && (
              <ul className="gr-starters">{starters.map((s, i) => <li key={i}>{s}</li>)}</ul>
            )}
          </div>
        )}

        {reviews.length > 0 && (
          <div className="gr-block">
            <button type="button" className="gr-toggle" onClick={() => setShowReviews((v) => !v)}>
              <i className={`fa fa-chevron-${showReviews ? 'up' : 'down'}`} />
              {showReviews ? 'Hide reviews' : `Show ${reviews.length} sampled reviews`}
            </button>
            {showReviews && (
              <div className="gr-reviews">
                {reviews.map((r, i) => (
                  <article key={i} className={`gr-review ${r.stars != null && r.stars <= 2 ? 'gr-review--neg' : ''}`}>
                    <div className="gr-review-top">
                      {r.stars != null && <Stars value={r.stars} />}
                      {text(r.author) && <span className="gr-review-author">{text(r.author)}</span>}
                      {formatDate(r.publishedAt) && <span className="gr-review-date">{formatDate(r.publishedAt)}</span>}
                      {r.ownerReplied && <span className="gr-review-replied"><i className="fa fa-reply" />answered</span>}
                    </div>
                    <p className="gr-review-text">{text(r.text)}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="gr-footer">
          {auditedAt && <span><i className="fa fa-clock" />Audited {auditedAt}</span>}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <i className="fa fa-map-location-dot" />Open in Maps
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
